export type DocumentCardKind =
  | "pdf"
  | "word"
  | "excel"
  | "powerpoint"
  | "text"
  | "generic";

const KIND_BY_EXTENSION: Record<string, DocumentCardKind> = {
  pdf: "pdf",
  doc: "word",
  docx: "word",
  xls: "excel",
  xlsx: "excel",
  ppt: "powerpoint",
  pptx: "powerpoint",
  txt: "text",
};

export function documentExtension(fileName: string | null | undefined, mimeType: string | null | undefined): string | null {
  const trimmedName = fileName?.trim();
  const fromName = trimmedName?.split(".").pop()?.toLowerCase();
  if (fromName && trimmedName && fromName !== trimmedName.toLowerCase()) {
    return fromName;
  }
  const mime = mimeType?.split(";")[0]?.trim().toLowerCase();
  if (!mime) {
    return null;
  }
  if (mime === "application/pdf") return "pdf";
  if (mime === "text/plain") return "txt";
  return null;
}

export function documentCardKind(
  fileName: string | null | undefined,
  mimeType: string | null | undefined,
): DocumentCardKind {
  const extension = documentExtension(fileName, mimeType);
  if (extension && KIND_BY_EXTENSION[extension]) {
    return KIND_BY_EXTENSION[extension];
  }
  const mime = mimeType?.split(";")[0]?.trim().toLowerCase();
  if (mime === "application/pdf") return "pdf";
  if (mime === "application/msword") return "word";
  if (mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
    return "word";
  }
  if (mime === "application/vnd.ms-excel") return "excel";
  if (mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
    return "excel";
  }
  if (mime === "application/vnd.ms-powerpoint") return "powerpoint";
  if (mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
    return "powerpoint";
  }
  if (mime === "text/plain") return "text";
  return "generic";
}

/** Human-readable size for messaging cards. Never raw `155,883B`. */
export function formatHumanFileSize(bytes: string | null | undefined): string | null {
  if (!bytes || !/^\d+$/.test(bytes)) {
    return null;
  }
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  if (value < 1024) {
    return `${value} B`;
  }
  if (value < 1024 * 1024) {
    const kb = value / 1024;
    const rounded = kb >= 100 ? Math.round(kb) : Math.round(kb * 10) / 10;
    return `${rounded} KB`;
  }
  const mb = value / (1024 * 1024);
  const rounded = mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10;
  return `${rounded} MB`;
}

export function documentKindAccentClass(kind: DocumentCardKind): string {
  switch (kind) {
    case "pdf":
      return "bg-destructive/12 text-destructive";
    case "word":
      return "bg-accent/12 text-accent";
    case "excel":
      return "bg-primary/12 text-primary";
    case "powerpoint":
      return "bg-accent-soft text-accent-hover";
    case "text":
      return "bg-ink/8 text-ink";
    case "generic":
      return "bg-surface-muted text-ink-muted";
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}
